import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Gift, Wand2, CreditCard, Share2, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { Button } from './ui/Button';

// Tour rápido pós-onboarding. Exibido UMA única vez. NAO pode ser dispensado
// — sem X, sem backdrop click, sem "Pular". O casal precisa chegar no ultimo
// passo e clicar em "Concluir" ou "Adicionar 1º presente"; só ai marcamos
// tutorialSeen=true no Firestore e o componente nao aparece mais.
export function WelcomeTour({ open, onFinish, publicLink }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  const steps = [
    {
      icon: Sparkles,
      title: 'Bem-vindos ao Mimo do Casal!',
      paragraphs: [
        'Em 4 passos rápidos a gente te mostra como o app funciona.',
        'É rápido — prometemos. Esse tour só aparece dessa vez.',
      ],
    },
    {
      icon: Gift,
      title: 'Meus Presentes',
      eyebrow: 'Menu lateral › Meus Presentes',
      paragraphs: [
        'É aqui que você monta a sua lista. Cada card tem título, valor, categoria e foto, e fica disponível pros convidados contribuírem.',
        'Você tem 3 formas de adicionar presentes:',
      ],
      bullets: [
        { label: 'Criar do zero', detail: 'monte um card com sua própria foto e descrição.' },
        { label: 'Biblioteca', detail: 'escolha entre presentes prontos com sugestão de valor.' },
        { label: 'IA Mágica', detail: 'na aba ao lado, a IA cria cenas únicas com vocês como protagonistas.' },
      ],
    },
    {
      icon: Wand2,
      title: 'IA Mágica',
      eyebrow: 'Menu lateral › IA Mágica',
      image: '/images/tutorial/spa-retreat.png',
      paragraphs: [
        'Aqui o algoritmo cria imagens personalizadas baseadas em fotos suas e da pessoa amada — como o exemplo acima.',
        'Você tem 50 imagens grátis por casamento. Pode descrever a cena que imagina pro card ou usar o gerador em lote (vários presentes de uma vez).',
      ],
    },
    {
      icon: CreditCard,
      title: 'Configure o recebimento',
      eyebrow: 'Menu lateral › Configurações › Pagamento',
      paragraphs: [
        'Sem isso os presentes não podem ser comprados. Vá em Configurações > Pagamento e preencha seus dados (Pix, CPF/CNPJ, endereço).',
        'Quando alguém presentear, o valor cai direto na sua carteira Mimo. Pra sacar pro seu Pix, o mínimo é R$ 100 e a taxa é R$ 5. Só o primeiro saque espera 7 dias — os seguintes não têm espera.',
      ],
    },
    {
      icon: Share2,
      title: 'Compartilhe sua página',
      paragraphs: [
        publicLink
          ? `Sua página pública já está no ar em ${publicLink}`
          : 'Sua página pública já está no ar com o slug que você escolheu.',
        'No topo do dashboard tem os botões "Copiar link" e "WhatsApp" pra mandar pros convidados quando estiver pronto.',
      ],
    },
  ];

  const current = steps[step];
  const isLast = step === steps.length - 1;
  const Icon = current.icon;

  const handleNext = () => {
    if (isLast) {
      onFinish?.();
      return;
    }
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const handleFinishAndGoToCards = () => {
    onFinish?.();
    navigate('/dashboard/cards');
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          {/* Backdrop inerte — propositalmente sem onClick. Fechar so via X
              ou "Concluir" pra evitar dismiss acidental quando o tour abre
              logo apos um click (mouseup atravessa para o backdrop novo). */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/45 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 24 }}
            transition={{ type: 'spring', damping: 30, stiffness: 320 }}
            className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl z-10 overflow-hidden"
          >
            <div className="bg-gradient-to-br from-rose-50 to-white px-5 sm:px-8 pt-9 pb-6">
              <div className="text-center">
                <motion.div
                  key={step}
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', damping: 18, stiffness: 240 }}
                  className="w-16 h-16 mx-auto rounded-2xl bg-rose-600 flex items-center justify-center shadow-lg shadow-rose-200 mb-3"
                >
                  <Icon size={26} className="text-white" />
                </motion.div>
              </div>
              {/* Altura mínima estável: dimensionada para caber o slide
                  mais alto (Meus Presentes, com bullets) sem deslocar os
                  demais passos. */}
              <div className="relative sm:min-h-[340px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.25 }}
                    className="text-center"
                  >
                    {current.eyebrow && (
                      <p className="text-[10px] font-bold uppercase tracking-wider text-rose-500 mb-1">{current.eyebrow}</p>
                    )}
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{current.title}</h3>
                    {current.image && (
                      <div className="mb-3 rounded-xl overflow-hidden border border-rose-100 shadow-sm">
                        <img
                          src={current.image}
                          alt="Exemplo de card gerado pela IA"
                          className="w-full aspect-[16/10] object-cover"
                        />
                      </div>
                    )}
                    <div className="space-y-2 text-left">
                      {current.paragraphs?.map((p, i) => (
                        <p key={i} className="text-sm text-gray-600 leading-relaxed">{p}</p>
                      ))}
                    </div>
                    {current.bullets && (
                      <ul className="mt-3 space-y-2 text-left">
                        {current.bullets.map((b, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-rose-400 flex-shrink-0" />
                            <span className="text-sm text-gray-600 leading-relaxed">
                              <strong className="text-gray-800">{b.label}</strong> — {b.detail}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            <div className="px-4 sm:px-6 py-3 flex items-center justify-center gap-1.5 bg-white">
              {steps.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === step ? 'w-6 bg-rose-500' : i < step ? 'w-1.5 bg-rose-300' : 'w-1.5 bg-gray-200'
                  }`}
                />
              ))}
            </div>

            <div className="px-4 sm:px-6 pb-5 sm:pb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="order-2 sm:order-1 flex justify-center sm:justify-start">
                {step > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => setStep((s) => Math.max(0, s - 1))}>
                    <ArrowLeft size={14} /> Voltar
                  </Button>
                )}
              </div>
              <div className="order-1 sm:order-2 flex flex-col sm:flex-row sm:items-center gap-2">
                {isLast && (
                  <Button variant="secondary" size="md" onClick={handleFinishAndGoToCards}>
                    Adicionar 1º presente <ArrowRight size={14} />
                  </Button>
                )}
                <Button onClick={handleNext}>
                  {isLast ? (
                    <>Concluir <Check size={14} /></>
                  ) : (
                    <>Próximo <ArrowRight size={14} /></>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
